import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import Tooltip from 'common/common/Tooltip';
import { TrackEventContext } from 'common/containers/EventContainer';
import { ShowIntercomContext } from 'common/containers/IntercomContainer';
import LandingAhaHelmet from 'common/helmets/LandingAhaHelmet';
import Button from 'common/inputs/Button';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LandingLogos from 'common/LandingLogos';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import UppercaseHeader from 'common/UppercaseHeader';
import withContexts from 'common/util/withContexts';
import AhaLogo from 'img/landing/aha-logo.webp';
import AhaToCanny from 'img/landing/aha-to-canny.webp';
import Chris from 'img/landing/chris.webp';
import GoogleLogo from 'img/landing/google-logo.webp';
import Ida from 'img/landing/ida.webp';
import MercuryWordmark from 'img/landing/mercury-wordmark.webp';
import MicrosoftTeamsLogo from 'img/landing/microsoft-teams-logo.webp';
import OktaLogo from 'img/landing/okta-logo.webp';
import OneLoginLogo from 'img/landing/onelogin-logo.webp';
import SegmentLogo from 'img/landing/segment-logo.webp';
import Subdomain1x from 'img/landing/subdomain-1x.webp';
import Subdomain2x from 'img/landing/subdomain-2x.webp';

import CannyLogo from 'img/canny-wordmark-white.svg';
import GitHubLogo from 'img/landing/github-logo.svg';
import IntercomLogo from 'img/landing/intercom-logo.svg';
import JiraLogo from 'img/landing/jira-logo.svg';
import SalesforceLogo from 'img/landing/salesforce-logo.svg';
import SlackLogo from 'img/landing/slack-logo.svg';
import ZapierLogo from 'img/landing/zapier-logo.svg';
import ZendeskLogo from 'img/landing/zendesk-logo.svg';

import 'css/components/www/_LandingComparisonPageNew.scss';

class LandingAha extends Component {
  static propTypes = {
    showIntercom: PropTypes.func,
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Aha Page');
  }

  render() {
    return (
      <div className="landingAha landingComparisonPage">
        <LandingAhaHelmet />
        <div className="mainContentContainer">
          <div className="header">
            <div className="left">
              <h1>Canny is a focused, modern Aha&nbsp;alternative</h1>
              <p className="text">Aha wasn’t designed for user feedback. Canny&nbsp;was.</p>
              <p className="text">
                Aha Ideas is a user feedback afterthought. We built Canny because we know the
                importance of having an easy to use (but powerful) tool to collect, manage, and
                track user&nbsp;feedback.
              </p>
              <Link className="cta" to="/register">
                <Button buttonType="greenButton" value="Get Started" />
              </Link>
            </div>
            <LazyLoadedImage
              className="headerImg"
              loading="eager"
              src={Subdomain1x}
              srcSet={`${Subdomain1x} 1x, ${Subdomain2x} 2x`}
              alt="A feedback board in Canny, an Aha alternative, displaying users’ feature requests"
            />
          </div>
          <div className="section">
            <h2 className="sectionHeader">Table of contents</h2>
            <div className="sectionContent">
              <a href="#quick-look" className="text toc-option">
                <p className="number">01</p>
                <p className="option-text">
                  Quick look: Why people prefer Canny over Aha&nbsp;Ideas
                </p>
              </a>
              <a href="#compared" className="text toc-option">
                <p className="number">02</p>
                <p className="option-text">Comparison: Canny as an alternative to Aha&nbsp;Ideas</p>
              </a>
              <a href="#pricing" className="text toc-option">
                <p className="number">03</p>
                <p className="option-text">
                  Canny’s pricing is more cost-effective than Aha Ideas&nbsp;pricing
                </p>
              </a>
              <a href="#integrations" className="text toc-option">
                <p className="number">04</p>
                <p className="option-text">
                  What integrations does Aha Ideas have? What about&nbsp;Canny?
                </p>
              </a>
              <a href="#customers" className="text toc-option">
                <p className="number">05</p>
                <p className="option-text">Canny customers and use&nbsp;cases</p>
              </a>
              <a href="#migration" className="text toc-option">
                <p className="number">06</p>
                <p className="option-text">Switching to Canny from Aha&nbsp;Ideas</p>
              </a>
            </div>
          </div>
          <div className="section" id="quick-look">
            <h2 className="sectionHeader">
              Quick look: Why people prefer Canny over Aha&nbsp;Ideas
            </h2>
            <div className="sectionContent">
              <div className="comparisonChart">
                <div className="comparisonLogoHeader">
                  <div className="canny">
                    <LazyLoadedImage src={CannyLogo} alt="Canny logo" />
                  </div>
                  <div className="competitorLogo aha">
                    <LazyLoadedImage src={AhaLogo} alt="Aha Logo" />
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">Only the features you&nbsp;need</h4>
                    <p className="description">
                      We’ll integrate with your existing workflow and allow other tools to do
                      their&nbsp;jobs.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Feature bloated</h4>
                    <p className="description">
                      Half the battle is figuring out which features you want to turn&nbsp;on.
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">Simple, straightforward design</h4>
                    <p className="description">
                      Our widgets embed seamlessly into your&nbsp;product.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Outdated user interface</h4>
                    <p className="description">
                      Their widgets will look out of place in your slick&nbsp;app.
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">First class support</h4>
                    <p className="description">
                      You won’t need us often but when you do, we’ll be&nbsp;here.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Poor support</h4>
                    <p className="description">
                      Are you a top tier customer? No? Looks like you’re on your&nbsp;own.
                    </p>
                  </div>
                </div>
                <div className="comparison">
                  <div className="point">
                    <h4 className="title">Straightforward</h4>
                    <p className="description">
                      We have plans that work for early startups through enterprise.{' '}
                      <Link to="/register">Try&nbsp;it&nbsp;now</Link>.
                    </p>
                  </div>
                  <div className="point">
                    <h4 className="title">Complicated plans</h4>
                    <p className="description">
                      You're charged per user, per product, and it's tough to see what you'll
                      actually&nbsp;pay.
                    </p>
                  </div>
                </div>
              </div>
              <div className="quoteContainer">
                <div className="quote">
                  <p className="text">
                    "We evaluated a number of different feedback solutions and Canny was the clear
                    winner. It was simple to configure, connect to Jira, Slack, Zendesk and
                    Salesforce, and open to our entire team. It has not only helped us organize
                    feedback, but it has deepened our connection to&nbsp;customers."
                  </p>
                </div>
                <div className="credit">
                  <LazyLoadedImage src={Chris} alt="Photo of Chris Jones" />
                  <p className="text">
                    <p className="name">Chris Jones</p>
                    <h4 className="title founderTitle">
                      Chief Product Officer at{' '}
                      <a href="https://amperity.com/" rel="noopener" target="_blank">
                        Amperity
                      </a>
                    </h4>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="section" id="compared">
            <h2 className="sectionHeader">Canny compared to Aha&nbsp;Ideas</h2>
            <div className="sectionContent">
              <p className="text">
                Aha’s core focus is roadmapping software. It works well as a roadmap tool.
              </p>
              <p className="text">
                But, Aha’s feedback management feature, Aha Ideas, was added as an afterthought.
              </p>
              <p className="text">
                With Ideas, collecting and managing user feedback is an add on. So, it’s not as easy
                to use or baked into the&nbsp;tool.
              </p>
              <p className="text">
                Your options are limited with Aha Ideas. They integrate with fewer tools, so it's
                harder to capture feedback. They don't offer good tools for managing duplicate
                feedback. It’s confusing for your customers to use (which means less feedback for
                your&nbsp;team).
              </p>
              <p className="text">
                To put it bluntly, Aha is a product management software trying to do user feedback
                on the&nbsp;side.
              </p>
              <p className="text">
                Canny is a product management tool built around the idea that user feedback is
                vital. It’s not an afterthought; it should inform your roadmap and
                product&nbsp;decisions.
              </p>
              <div className="quoteContainer">
                <div className="quote">
                  <p className="text">
                    "The fact that I can engage directly with product and engineering on feedback
                    and deployment is&nbsp;HUGE."
                  </p>
                </div>
                <div className="credit">
                  <LazyLoadedImage src={Chris} alt="Photo of Chris Jones" />
                  <p className="text">
                    <p className="name">Chris Jones</p>
                    <h4 className="title founderTitle">
                      Chief Product Officer at{' '}
                      <a href="https://amperity.com/" rel="noopener" target="_blank">
                        Amperity
                      </a>
                    </h4>
                  </p>
                </div>
              </div>
              <p className="text">
                Feedback is the backbone of our tool, not a tacked-on&nbsp;feature.
              </p>
            </div>
          </div>
          <div className="section" id="pricing">
            <h2 className="sectionHeader">
              Canny’s pricing is more cost-effective than Aha Ideas&nbsp;pricing
            </h2>
            <div className="sectionContent">
              <p className="text">On the surface, Aha Ideas and Canny cost about the&nbsp;same.</p>
              <p className="text">
                At the lowest tier, Aha’s pricing is $59 a month, paid annually ($74 for
                month-to-month). Canny offers a generous Free plan. Both Canny and Aha offer scaled
                plans for larger&nbsp;businesses.
              </p>
              <p className="text">
                But, Aha charges per seat. So, only one product manager (or another team member)
                will be able to use Aha and take advantage of Aha&nbsp;Ideas.
              </p>
              <p className="text">
                With Canny, 5 admin seats are included in your plan, and you can have unlimited free
                "contributor" admins. With Aha Ideas, each contributor requires a paid&nbsp;seat.
              </p>
              <p className="text">
                Keep in mind – Canny also includes tools for managing your roadmap and release
                notes. Everything you need to close the feedback loop. With Aha software, you have
                to pay for each of those tools separately. Per tool, per&nbsp;user.
              </p>
              <p className="text">
                Your costs can rapidly increase if you invite your whole team to&nbsp;Aha.
              </p>
              <p className="text">
                <Link to="/pricing">Canny’s pricing</Link> is straightforward and offers more value.
                You can sign up with one of our self-serve plans right away — no need to chat with
                our team (unless you want&nbsp;to).
              </p>
              <div className="pricingContainer">
                <div className="pricingPlans">
                  <div className="pricingPlan">
                    <p className="planName">Free</p>
                    <div className="price">
                      <p className="number">$0</p>
                      <p className="perMonth">/ mo</p>
                    </div>
                  </div>
                  <div className="pricingPlan">
                    <p className="planName">Core, starts at</p>
                    <div className="price">
                      <p className="number">$19</p>
                      <p className="perMonth">/ mo</p>
                    </div>
                  </div>
                  <div className="pricingPlan">
                    <p className="planName">Pro, starts at</p>
                    <div className="price">
                      <p className="number">$79</p>
                      <p className="perMonth">/ mo</p>
                    </div>
                  </div>
                </div>
                <div className="morePricing">
                  <p className="morePricingText">
                    More details and custom pricing options&nbsp;available
                  </p>
                  <Link className="cta" to="/pricing">
                    View&nbsp;pricing
                  </Link>
                </div>
              </div>
              <p className="text">
                Need a demo, or pricing that’s tailored to your specific needs? You got it. We’re
                standing by—you’ll be able to{' '}
                <Link to="/request-demo">hop on a call with our team</Link> and discuss
                your&nbsp;options.
              </p>
            </div>
          </div>
          <div className="section" id="integrations">
            <h2 className="sectionHeader">
              What integrations does Aha Ideas have? What about&nbsp;Canny?
            </h2>
            <div className="sectionContent">
              <p className="text">
                Both Canny and Aha integrate with the tools you already use. Since Aha is an older
                tool that’s been around for a long time, it has a lot of&nbsp;integrations.
              </p>
              <p className="text">
                But, Aha’s integrations are built for their roadmapping tool specifically—not for
                Aha Ideas. While Aha integrates with a lot of tools overall, Aha Ideas&nbsp;doesn’t.
              </p>
              <p className="text">
                We already have many <Link to="/integrations">integrations</Link> with the tools you
                know and love. And we’re always adding new&nbsp;integrations!
              </p>
              <div className="integrationsComparison">
                <div className="comparisonLogoHeader connected">
                  <div className="canny">
                    <LazyLoadedImage src={CannyLogo} alt="Canny Logo" />
                  </div>
                  <div className="both half">Both</div>
                </div>
                <div className="integrationsGrids">
                  <div className="integrationsGrid">
                    <div className="integrationsGridInner">
                      <Link to="/integrations/jira" className="integration jiraImage">
                        <Tooltip position="top" value="Jira">
                          <LazyLoadedImage src={JiraLogo} alt="Jira logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/intercom" className="integration intercomImage">
                        <Tooltip position="top" value="Intercom">
                          <LazyLoadedImage src={IntercomLogo} alt="Intercom logo" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://zapier.com/apps/canny/integrations"
                        rel="noopener"
                        className="integration zapierImage">
                        <Tooltip position="top" value="Zapier">
                          <LazyLoadedImage src={ZapierLogo} alt="Zapier logo" />
                        </Tooltip>
                      </a>
                      <Link to="/integrations/github" className="integration githubImage">
                        <Tooltip position="top" value="GitHub">
                          <LazyLoadedImage src={GitHubLogo} alt="GitHub logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/segment" className="integration segmentImage">
                        <Tooltip position="top" value="Segment">
                          <LazyLoadedImage src={SegmentLogo} alt="Segment logo" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://help.canny.io/en/articles/4343053-microsoft-teams-integration"
                        className="integration microsoftTeamsImage">
                        <Tooltip position="top" value="Microsoft Teams">
                          <LazyLoadedImage src={MicrosoftTeamsLogo} alt="Microsoft Teams logo" />
                        </Tooltip>
                      </a>
                      <div className="integration">
                        <Tooltip position="top" value="OneLogin">
                          <LazyLoadedImage src={OneLoginLogo} alt="OneLogin logo" />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                  <div className="integrationsGrid">
                    <div className="integrationsGridInner">
                      <Link to="/integrations/salesforce" className="integration salesforceImage">
                        <Tooltip position="top" value="Salesforce">
                          <LazyLoadedImage src={SalesforceLogo} alt="Salesforce logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/zendesk" className="integration zendeskImage">
                        <Tooltip position="top" value="Zendesk">
                          <LazyLoadedImage src={ZendeskLogo} alt="Zendesk logo" />
                        </Tooltip>
                      </Link>
                      <Link to="/integrations/slack" className="integration slackImage">
                        <Tooltip position="top" value="Slack">
                          <LazyLoadedImage src={SlackLogo} alt="Slack logo" />
                        </Tooltip>
                      </Link>
                      <a
                        href="https://help.canny.io/en/articles/4029303-okta-sso-integration"
                        className="integration oktaImage">
                        <Tooltip position="top" value="Okta">
                          <LazyLoadedImage src={OktaLogo} alt="Okta logo" />
                        </Tooltip>
                      </a>
                      <a
                        href="https://help.canny.io/en/articles/4228880-g-suite-integration"
                        className="integration googleImage">
                        <Tooltip position="top" value="Google Workspace">
                          <LazyLoadedImage src={GoogleLogo} alt="Google logo" />
                        </Tooltip>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text">
                You can{' '}
                <Link to="/integrations">learn more about Canny’s integrations&nbsp;here</Link>.
              </p>
            </div>
          </div>
          <div className="section" id="customers">
            <h2 className="sectionHeader">Canny customers and use&nbsp;cases</h2>
            <div className="sectionContent">
              <p className="text">
                Aha Ideas can work if you’re a big company that’s already using Aha for
                product&nbsp;management.
              </p>
              <p className="text">
                But, if you’re interested in truly making the most out of customer feedback and
                integrating it into your product management process (rather than adding it on as an
                afterthought), Aha Ideas just isn’t the best option&nbsp;available.
              </p>
              <p className="text">
                Because Canny is a flexible, reliable, and customizable solution, we have customers
                all the way from 2-person startups to billion dollar companies like{' '}
                <Link to="/case-studies">CircleCI&nbsp;and&nbsp;Mercury</Link>.
              </p>
              <Link className="customerItemContents mercury" to="/case-studies/mercury">
                <p className="text">
                  <div className="top">
                    <div className="wordmark">
                      <LazyLoadedImage src={MercuryWordmark} alt="Mercury logo" />
                    </div>
                    <p className="description">
                      How Mercury found a drastic improvement in efficiency and&nbsp;productivity
                    </p>
                  </div>
                  <UppercaseHeader>
                    Read their story <span className="icon-chevron-right" />
                  </UppercaseHeader>
                </p>
                <LazyLoadedImage className="profile" src={Ida} alt="Photo of Ida Ström" />
              </Link>
              <p className="text">
                And, Canny isn’t just for collecting feedback from customers.{' '}
                <Link to="/use-cases/internal-feedback">Internal teams</Link> also{' '}
                <Link to="/features/product-roadmap">
                  use it to link user feedback to their product roadmap
                </Link>{' '}
                and <Link to="/features/product-changelog">announce feature&nbsp;updates</Link>.
              </p>
            </div>
          </div>
          <div className="section importData" id="migration">
            <h2 className="sectionHeader">Switching to Canny from Aha&nbsp;Ideas</h2>
            <div className="sectionContent">
              <p className="text">
                Currently an Aha customer? Many of our customers made the switch from Aha to
                Canny—so many that we've automated the whole&nbsp;process.
              </p>
              <LazyLoadedImage
                src={AhaToCanny}
                alt="Switch to Canny, the Aha Ideas alternative, with free data migration"
              />
              <p className="text">
                Switching is easy and free. We can migrate all of your data from Aha to Canny within
                48 hours. Reach out to us to get&nbsp;started.
              </p>
            </div>
          </div>
          <LandingLogos />
          <LandingFooterCTA style="solid" />
        </div>
      </div>
    );
  }
}

export default compose(
  withContexts({
    showIntercom: ShowIntercomContext,
    trackEvent: TrackEventContext,
  }),
  hot,
)(LandingAha);
